/**
 * Use Users Migration Hook
 *
 * Custom hooks for managing migration users with TanStack Query.
 * Handles caching, refetching, mutations, and error states automatically.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  fetchMigrationUsers,
  fetchMigrationUserById,
  createMigrationUser,
  updateMigrationUser,
  deleteMigrationUser,
} from '@/lib/api';
import { useAuth } from '@/state/auth-context';
import type {
  MigrationUser,
  ListMigrationUsersResponse,
  ListMigrationUsersParams,
  CreateMigrationUserRequest,
  UpdateMigrationUserRequest,
  DeleteMigrationUserResponse,
} from '@/types/user-migration';

/**
 * Use Migration Users
 * Fetches paginated list of migration users with filters
 *
 * @param params - Query parameters for filtering and pagination
 * @returns TanStack Query result with migration users data
 */
export function useMigrationUsers(
  params: ListMigrationUsersParams
): UseQueryResult<ListMigrationUsersResponse> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['migrationUsers', params],
    queryFn: async () => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchMigrationUsers(params, token);
    },
    staleTime: 30000, // 30 seconds - data is considered fresh for 30s
    gcTime: 300000, // 5 minutes - cache is garbage collected after 5 minutes
    retry: 2, // Retry failed requests twice
    refetchOnWindowFocus: true, // Refetch when window regains focus
    refetchOnReconnect: true, // Refetch when network reconnects
  });
}

/**
 * Use Migration User by ID
 * Fetches a single migration user by cognitoId
 *
 * @param cognitoId - Cognito user ID
 * @param enabled - Whether the query should run (default: true)
 * @returns TanStack Query result with single migration user data
 */
export function useMigrationUserById(
  cognitoId: string | null,
  enabled = true
): UseQueryResult<MigrationUser> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['migrationUser', cognitoId],
    queryFn: async () => {
      if (!cognitoId) {
        throw new Error('No cognito ID provided');
      }

      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchMigrationUserById(cognitoId, token);
    },
    enabled: enabled && cognitoId !== null, // Only run query if enabled and ID is provided
    staleTime: 60000, // 1 minute - individual users change less frequently
    gcTime: 600000, // 10 minutes - keep in cache longer
    retry: 2,
  });
}

/**
 * Use Create Migration User
 * Mutation hook for creating a new migration user
 *
 * @returns TanStack Mutation result
 */
export function useCreateMigrationUser(): UseMutationResult<
  MigrationUser,
  Error,
  CreateMigrationUserRequest
> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateMigrationUserRequest) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return createMigrationUser(data, token);
    },
    onSuccess: () => {
      // Invalidate migration users list to refetch with new user
      queryClient.invalidateQueries({ queryKey: ['migrationUsers'] });
    },
  });
}

/**
 * Use Update Migration User
 * Mutation hook for updating an existing migration user
 *
 * @returns TanStack Mutation result
 */
export function useUpdateMigrationUser(): UseMutationResult<
  MigrationUser,
  Error,
  { cognitoId: string; data: UpdateMigrationUserRequest }
> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cognitoId,
      data,
    }: {
      cognitoId: string;
      data: UpdateMigrationUserRequest;
    }) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return updateMigrationUser(cognitoId, data, token);
    },
    onSuccess: (_, variables) => {
      // Invalidate both the list and the specific user query
      queryClient.invalidateQueries({ queryKey: ['migrationUsers'] });
      queryClient.invalidateQueries({
        queryKey: ['migrationUser', variables.cognitoId],
      });
    },
  });
}

/**
 * Use Delete Migration User
 * Mutation hook for deleting a migration user
 *
 * @returns TanStack Mutation result
 */
export function useDeleteMigrationUser(): UseMutationResult<
  DeleteMigrationUserResponse,
  Error,
  string
> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cognitoId: string) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return deleteMigrationUser(cognitoId, token);
    },
    onSuccess: (_, cognitoId) => {
      // Invalidate both the list and the specific user query
      queryClient.invalidateQueries({ queryKey: ['migrationUsers'] });
      queryClient.invalidateQueries({ queryKey: ['migrationUser', cognitoId] });
    },
  });
}
